/**
 * Shared radar/spider geometry — the SINGLE source used by both the big "How
 * it's landing" EmotionSpider and the Brief crowd mini-spiders, so every radar
 * rounds its corners identically (no more drift between two copies). Pure math;
 * presentation only, never feeds scoring.
 */

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));

/** Radar vertices as (x,y) pairs, clockwise from the top vertex. */
export function radarXY(
  values: number[],
  radius = 40,
  cx = 52,
  cy = 50,
): Array<[number, number]> {
  return values.map((v, i) => {
    const angle = (-90 + (360 / values.length) * i) * (Math.PI / 180);
    const r = clamp01(v) * radius;
    return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
  });
}

/** Radar axis points as an SVG `points` string (for grid rings / straight fills). */
export function radarPoints(values: number[], radius = 40, cx = 52, cy = 50): string {
  return radarXY(values, radius, cx, cy)
    .map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`)
    .join(" ");
}

/**
 * A CLOSED smooth path through the vertices via a Catmull-Rom spline converted to
 * cubic béziers (tension 1/6). Rounds the radar corners so zones read as soft
 * blobs instead of hard-angled polygons, while still passing through every axis.
 */
export function smoothClosedPath(pts: Array<[number, number]>): string {
  const n = pts.length;
  if (n < 3) return pts.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(" ");
  const p = (i: number) => pts[((i % n) + n) % n]!;
  let d = `M ${p(0)[0].toFixed(2)} ${p(0)[1].toFixed(2)}`;
  for (let i = 0; i < n; i++) {
    const p0 = p(i - 1);
    const p1 = p(i);
    const p2 = p(i + 1);
    const p3 = p(i + 2);
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)}, ${p2[0].toFixed(2)} ${p2[1].toFixed(2)}`;
  }
  return `${d} Z`;
}

/** A smoothed, closed radar zone path for the given per-axis values. */
export function radarSmoothPath(
  values: number[],
  radius = 40,
  cx = 52,
  cy = 50,
): string {
  return smoothClosedPath(radarXY(values, radius, cx, cy));
}
